import { ReactElement, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { BsInfoCircle } from "react-icons/bs";
import { useLocation } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import AlertModal from "../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import SearchBar from "../../../components/searchBar/SearchBar";
import Title from "../../../components/title/Title";
import {
  getFaultDetailsByRefNo,
  getFaultProgressByRefNo,
} from "../../../services/fault";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import { IFaultProgress } from "../../../interfaces/FaultProgress";
import inProgressIcon from "../../../assets/images/icons/faultProgress/loading sc.gif";
import completedIcon from "../../../assets/images/icons/faultProgress/completed.svg";
import disabledIcon from "../../../assets/images/icons/faultProgress/disabled.svg";
import styles from "./FaultProgress.module.scss";
import useMediaQuery from "@mui/material/useMediaQuery";
import TooltipPopup from "../../../components/tooltipPopup/TooltipPopup";
import { IFaultDetails } from "../../../interfaces/faultDetails";
import { useNavigate } from "react-router-dom";

const FaultProgress = () => {
  const { state } = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [faultStatus, setFaultStatus] = useState<IFaultProgress>();
  const [faultStatusV2, setFaultStatusV2] = useState<IFaultDetails>();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [searchFaultId, setSearchFaultId] = useState<string>();

  const isMobile = useMediaQuery("(max-width: 600px)");
  const [forceSearch, setForceSearch] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (state) {
      setSearchFaultId((state as any).srNumber);
    }
  }, [state]);

  useEffect(() => {
    if (searchFaultId) {
      setFaultStatus(undefined);

      const data = {
        faultReferenceNumber: searchFaultId.trim(),
        customerReferenceNumber: getCustomerReferenceNumber(),
      };
      setLoading(true);
      getFaultProgressByRefNo(data)
        .then((data: IFaultProgress) => {
          setFaultStatus(data);
          setLoading(false);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setLoading(false);
        });
    }
  }, [searchFaultId, forceSearch]);

  useEffect(() => {
    if (searchFaultId) {
      setFaultStatusV2(undefined);
      const data = {
        faultReferenceNumber: searchFaultId.trim(),
        customerReferenceNumber: getCustomerReferenceNumber(),
      };
      setLoading(true);
      getFaultDetailsByRefNo(data)
        .then((data: IFaultDetails) => {
          setFaultStatusV2(data);
          console.log(faultStatusV2);
          // setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  }, [searchFaultId, forceSearch]);

  const searchHandler = (value: string) => {
    if (!value) {
      return;
    }

    setSearchFaultId(value);
    setForceSearch((prev) => !prev);
  };

  const detailsItem = (title: string, value: string | ReactElement | null) => (
    <>
      {value && (
        <Row>
          <Col className={[styles.detailsItem].join(" ")}>
            <p className={styles.title}>{title}</p>
            <div className={styles.value}>{value}</div>
          </Col>
        </Row>
      )}
    </>
  );

  const getShortAddress = (address: string | null): string => {
    if (!address) {
      return "";
    }
    const addressArray = address.split(",");
    return addressArray[addressArray?.length - 2];
  };

  interface Step {
    number: number;
    title: string;
    description?: string;
    active: boolean;
    icon?: JSX.Element;
  }

  const steps: Step[] = [
    {
      number: 1,
      title: "Reported",
      description: faultStatus?.reportedDate,
      active: faultStatus?.reported ?? false,
      icon: (
        <img
          className={styles.resizedGif}
          src={faultStatus?.reported ? completedIcon : disabledIcon}
          alt="Reported"
        />
      ),
    },
    {
      number: 2,
      title: "Troubleshooting by SLT Head Office Team",
      // description: faultStatus?.reportedDate,
      active: faultStatus?.hqTeams !== null || faultStatus?.completed === true,
      icon: (
        <img
          className={styles.resizedGif}
          src={
            faultStatus?.completed === true
              ? completedIcon
              : faultStatus?.hqTeams?.includes("Attending")
              ? inProgressIcon
              : faultStatus?.hqTeams?.includes("Attended")
              ? completedIcon
              : disabledIcon
          }
          alt="Troubleshooting"
        />
      ),
    },
    {
      number: 3,
      title: "Troubleshooting by Field Team",
      active:
        faultStatus?.regionalTeams !== null || faultStatus?.completed === true,
      icon: (
        <img
          className={styles.resizedGif}
          src={
            faultStatus?.completed === true
              ? completedIcon
              : faultStatus?.regionalTeams?.includes("Attending")
              ? inProgressIcon
              : faultStatus?.regionalTeams?.includes("Attended")
              ? completedIcon
              : disabledIcon
          }
          alt="Field Team"
        />
      ),
    },
    {
      number: 4,
      title: "Confirmed & Completed",
      description: faultStatus?.completedDate ?? undefined,
      active:
        (faultStatus?.regionalTeams?.includes("Attended") &&
          faultStatus?.hqTeams?.includes("Attended")) ||
        faultStatus?.completed === true,

      icon: (
        <img
          className={styles.resizedGif}
          src={
            faultStatus?.completed
              ? completedIcon
              : faultStatus?.regionalTeams?.includes("Attended") &&
                faultStatus?.hqTeams?.includes("Attended")
              ? inProgressIcon
              : disabledIcon
          }
          alt="Complete"
        />
      ),
    },
  ];

  return (
    <div className="page-container">
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/tracker/status/faultProgress",
            title: "Service Complaint Status",
          },
        ]}
      />
      <Title>Service Complaint Status</Title>
      <Row className="justify-content-center mt-2">
        <Col className="col-md-5">
          <SearchBar
            placeholder="Fault Reference"
            onSearch={searchHandler}
            defaultValue={searchFaultId}
          />
        </Col>
      </Row>

      <Row>
        <Col className={[styles.container, "mt-4 py-5 px-5"].join(" ")}>
          {faultStatusV2 && (
            <Col>
              {detailsItem(
                "",
                <div className="bg-light rounded-3 p-3 mb-2 shadow-sm">
                  <Row className="align-items-center g-2">
                    <Col className="col-auto">
                      <div className="d-flex align-items-center gap-2">
                        <span className="font-popins text-dark fs-6 fw-semibold">
                          Branch Location:{" "}
                          {getShortAddress(faultStatusV2?.serviceAddress)}
                        </span>
                        <TooltipPopup
                          tooltip={faultStatusV2?.serviceAddress || ""}
                          placement="top"
                        >
                          <div className="text-primary-hover">
                            <BsInfoCircle
                              className="text-muted transition-all"
                              size={18}
                              style={{ cursor: "help" }}
                            />
                          </div>
                        </TooltipPopup>
                      </div>
                    </Col>{" "}
                    {faultStatus && (
                      <p
                        style={{
                          color: "#0A3D91",
                          fontWeight: 600,
                          fontSize: 16,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {" "}
                        Service ID : {faultStatus.serviceId}
                      </p>
                    )}
                  </Row>
                </div>
              )}
            </Col>
          )}
          {faultStatus ? (
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              justifyContent="center"
              alignItems="center"
              gap={2}
              mt={4}
              sx={{
                flexWrap: "wrap",
              }}
            >
              {steps.map((step, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width={isMobile ? "100%" : "260px"}
                  height={isMobile ? "auto" : "110px"}
                  borderRadius="15px"
                  padding={isMobile ? "10px 15px" : "10px 20px"}
                  sx={{
                    background: step.active
                      ? "linear-gradient(to right, #D4EAF7, #F2F8FF)"
                      : "#E5E5E5",
                    color: step.active ? "#0A3D91" : "#A0A0A0",
                    boxShadow: step.active ? 3 : 0,
                    transition: "0.3s",
                    marginBottom: isMobile ? "20px" : "0",
                  }}
                >
                  <Box
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    m={isMobile ? 1 : 1}
                    mr={isMobile ? 2 : 1}
                    sx={{
                      backgroundColor: step.active ? "#51B848" : "#BDBDBD",
                      color: "white",
                      fontSize:
                        step.number === 2 || step.number === 3
                          ? "16px"
                          : "18px",

                      fontWeight: "bold",
                    }}
                  >
                    {step.icon}
                  </Box>

                  <Box
                    width="4px"
                    height="35px"
                    mx={1}
                    sx={{
                      backgroundColor: step.active ? "#0A3D91" : "#A0A0A0",
                    }}
                  />

                  <Box>
                    <Typography
                      fontWeight={step.active ? "bold" : "normal"}
                      fontSize={isMobile ? "12px" : "14px"}
                      sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                    >
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography
                        fontSize={isMobile ? "10px" : "12px"}
                        sx={{ color: step.active ? "#0A3D91" : "#A0A0A0" }}
                      >
                        {step.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <EmptyPlaceholder />
          )}
        </Col>
      </Row>

      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          sx={{
            minWidth: "56px",
            width: "350px",
            height: "50px",
            boxShadow: 3,
            "&:hover": {
              boxShadow: 6,
            },
          }}
          onClick={() => {
            navigate(`/tracker/status/faultProgress/ongoing`);
          }}
        >
          View All Ongoing Service Complaints
        </Button>
      </Box>

      {/* error message */}
      <AlertModal
        show={!!errorMessage}
        buttons={[
          {
            variant: "danger",
            text: "OK",
            onClick: () => {
              setErrorMessage(undefined);
            },
          },
        ]}
        variant={"error"}
      >
        {errorMessage}
      </AlertModal>
    </div>
  );
};

export default FaultProgress;
